/* eslint-disable camelcase */
import axios, { AxiosResponse } from 'axios';
import { generateUniqueId } from '../../utils/generateUniqueId';
import { DafAnonymousAuthConfig, DafAuthError, DafAnonymousAuthToken, DafGrantType, DafAuthResponse } from '../../types';
import { dafErrorMap } from '../../utils/dafErrorMap';
import { stringifyScopes } from '../../utils/stringifyScopes';
import { DafAuthStrategy } from './DafAuthStrategy';

/**
 * DAF external/internal auth strategy for nab:anonymous grant type
 */
export class DafAnonymousAuthStrategy extends DafAuthStrategy<DafAnonymousAuthConfig> {
    constructor(dafEndpoint: string, protected clientId: string, protected isSeed: boolean = true) {
        super(dafEndpoint);
    }

    public supports(config: DafAnonymousAuthConfig) {
        return (
            super.supports(config) &&
            config.tokenType === 'Bearer' &&
            (config.context === 'external' || config.context === 'internal') &&
            config.grantType === DafGrantType.nabAnonymous &&
            !!config.isSeed === !!this.isSeed
        );
    }

    public async authenticate(config: DafAnonymousAuthConfig, retryCount: number = 0): Promise<DafAnonymousAuthToken> {
        try {
            const response = await axios.post<any, AxiosResponse<DafAuthResponse>>(
                this.dafEndpoint,
                {
                    client_id: this.clientId,
                    grant_type: DafGrantType.nabAnonymous,
                    scope: stringifyScopes(config.scopes) || ''
                },
                config.additionalRequestHeaders && {
                    headers: config.additionalRequestHeaders
                }
            );
            const { access_token, idleTimeout, expires_in } = response.data;
            return {
                id: generateUniqueId(),
                tokenValue: access_token,
                idleTimeout: idleTimeout ? idleTimeout * 1000 : undefined,
                issuedAt: Date.now(),
                expires: expires_in ? Date.now() + expires_in * 1000 : undefined,
                config
            };
        } catch (e) {
            if (e && e.response && e.response.status && dafErrorMap[`${e.response.status}`]) {
                const error = new Error(dafErrorMap[`${e.response.status}`]);
                return this.handleDafError(error, config, retryCount);
            }
            throw new Error(DafAuthError.UNKNOWN_ERROR);
        }
    }
}
